import { type HTMLAttributes, type ReactNode, useId, useMemo } from 'react';
import { clsx } from 'clsx';
import type { Space } from '../../../styles/tokens';
import { useIntersectionObserverAction } from '../../../util/intersection-observer';
import type { ResponsiveValue } from '../../../util/responsive';
import { HorizontalStack } from '../../alignment/horizontal-stack';
import { VerticalStack } from '../../alignment/vertical-stack';
import type { LinkElementType } from '../../link/base-link';
import { LoadingSpinner } from '../../loading/spinner';
import { DEFAULT_HEADING_LEVEL_3_TYPOGRAPHY, Typography, type TypographyProps } from '../../typography';
import { getReadonlyListLoadingRows, Item, type ReadonlyListItem } from './item/readonly-list-item';
import listStyles from '../list.module.scss';
import styles from './readonly-list.module.scss';

export interface ReadonlyListPagination {
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => Promise<any>;
}

export interface ReadonlyListProps extends Omit<HTMLAttributes<HTMLUListElement>, 'title'> {
  defaultLinkElement?: LinkElementType;
  defaultRenderWhenEmpty?: ReactNode;
  isPending?: boolean;
  items: ReadonlyListItem[];
  loadingRows?: number;
  pagination?: ReadonlyListPagination;
  renderWhenEmpty?: ReactNode;
  title?: ReactNode;
  titleGap?: ResponsiveValue<Space>;
  titleTypography?: TypographyProps;
  withLastChildBorder?: boolean;
}

export function ReadonlyList({
  className,
  defaultLinkElement = 'a',
  defaultRenderWhenEmpty = '--',
  isPending = false,
  items,
  loadingRows,
  pagination,
  renderWhenEmpty,
  title,
  titleGap = 7,
  titleTypography = DEFAULT_HEADING_LEVEL_3_TYPOGRAPHY,
  withLastChildBorder,
  ...rest
}: ReadonlyListProps) {
  const id = useId();
  const isRenderingEmptyState = Boolean(renderWhenEmpty && items.length === 0 && !isPending);
  const renderedLoadingRows = useMemo(
    () => (isRenderingEmptyState || (isPending && loadingRows) ? getReadonlyListLoadingRows(loadingRows || 5) : null),
    [isRenderingEmptyState, loadingRows, isPending],
  );
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = pagination || {};
  const [setObservedItemRef] = useIntersectionObserverAction(fetchNextPage, {
    disabled: !hasNextPage || isFetchingNextPage || !fetchNextPage,
  });
  const renderedItems = renderedLoadingRows || items;

  return (
    <VerticalStack className={styles.readonlyList} gap={titleGap}>
      {title ? (
        <Typography className={styles.title} {...titleTypography}>
          {title}
        </Typography>
      ) : null}

      <div className={styles.container}>
        <VerticalStack as="ul" className={clsx(listStyles.list, className)} {...rest}>
          {renderedItems.map((item, i) => (
            <Item
              defaultLinkElement={defaultLinkElement}
              defaultRenderWhenEmpty={defaultRenderWhenEmpty}
              idPrefix={id}
              isPending={isPending}
              key={item.id}
              {...item}
              ref={i === renderedItems.length - 1 ? setObservedItemRef : undefined}
              withLastChildBorder={withLastChildBorder}
            />
          ))}

          {isFetchingNextPage ? (
            <HorizontalStack align="center" blockAlign="center" className={listStyles.loader}>
              <LoadingSpinner height={16} />
            </HorizontalStack>
          ) : null}
        </VerticalStack>

        {isRenderingEmptyState ? (
          <div className={styles.emptyMessage}>
            {renderWhenEmpty ? (
              <Typography
                align="center"
                className={styles.content}
                lineHeight="1.625rem"
                size="1.125rem"
                tone="neutral-dk"
                variant="heading"
                weight="bold"
              >
                {renderWhenEmpty}
              </Typography>
            ) : null}
          </div>
        ) : null}
      </div>
    </VerticalStack>
  );
}
